import { useState } from 'react';
import { TOTAL_QUESTIONS, ESTIMATED_TIME_MINUTES } from '@/lib/questions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowRight, Clock, Shield, FileText } from 'lucide-react';
import { z } from 'zod';

const formSchema = z.object({
  name: z.string().trim().min(2, 'Please enter your name').max(100),
  email: z.string().trim().email('Please enter a valid email address').max(255),
  role: z.string().min(1, 'Please select your role'),
  otherRole: z.string().trim().max(100).optional(),
  practice_name: z.string().trim().max(200).optional(),
  consent: z.boolean().refine(val => val === true, 'You must accept to continue'),
}).refine(
  (data) => data.role !== 'other' || (data.otherRole && data.otherRole.length >= 2),
  { message: 'Please specify your role', path: ['otherRole'] }
);

interface IntroFormProps {
  onStart: (info: {
    name: string;
    email: string;
    role?: string;
    practice_name?: string;
  }) => void;
  loading?: boolean;
  totalQuestions: number;
}

export function IntroForm({ onStart, loading, totalQuestions }: IntroFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [otherRole, setOtherRole] = useState('');
  const [practiceName, setPracticeName] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const estimatedTime = ESTIMATED_TIME_MINUTES;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = formSchema.safeParse({
      name,
      email,
      role,
      otherRole: otherRole || undefined,
      practice_name: practiceName || undefined,
      consent,
    });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    // Use the custom role text if "other" was selected
    const finalRole = role === 'other' ? otherRole : role;

    onStart({
      name: result.data.name,
      email: result.data.email,
      role: finalRole || undefined,
      practice_name: result.data.practice_name,
    });
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-up">
      <div className="text-center mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
          GP Partnership Lab Inventory
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          A structured leadership, values, and partnership performance assessment 
          to help you understand your strengths and contribution style.
        </p>
      </div>

      {/* Info cards */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="card-premium text-center py-4">
          <Clock className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-sm font-medium text-foreground">{estimatedTime} minutes</p>
          <p className="text-xs text-muted-foreground">to complete</p>
        </div>
        <div className="card-premium text-center py-4">
          <FileText className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-sm font-medium text-foreground">{totalQuestions} questions</p>
          <p className="text-xs text-muted-foreground">130 statements</p>
        </div>
        <div className="card-premium text-center py-4">
          <Shield className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-sm font-medium text-foreground">Confidential</p>
          <p className="text-xs text-muted-foreground">individual report</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card-premium space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
            Your name *
          </label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Dr Jane Smith"
            className="rounded-xl"
          />
          {errors.name && (
            <p className="text-sm text-destructive mt-1">{errors.name}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            Email address *
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane.smith@practice.nhs.uk"
            className="rounded-xl"
          />
          {errors.email && (
            <p className="text-sm text-destructive mt-1">{errors.email}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Your report will be sent to this address
          </p>
        </div>

        <div>
          <label htmlFor="role" className="block text-sm font-medium text-foreground mb-2">
            Your role *
          </label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="rounded-xl">
              <SelectValue placeholder="Select your role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gp-partner">GP Partner</SelectItem>
              <SelectItem value="salaried-gp">Salaried GP</SelectItem>
              <SelectItem value="practice-manager">Practice Manager</SelectItem>
              <SelectItem value="pcn-leader">PCN Leader</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          {errors.role && (
            <p className="text-sm text-destructive mt-1">{errors.role}</p>
          )}
          
          {role === 'other' && (
            <div className="mt-3">
              <Input
                id="otherRole"
                value={otherRole}
                onChange={(e) => setOtherRole(e.target.value)}
                placeholder="Please specify your role"
                className="rounded-xl"
              />
              {errors.otherRole && (
                <p className="text-sm text-destructive mt-1">{errors.otherRole}</p>
              )}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="practice" className="block text-sm font-medium text-foreground mb-2">
            Practice name (optional)
          </label>
          <Input
            id="practice"
            value={practiceName}
            onChange={(e) => setPracticeName(e.target.value)}
            placeholder="Your practice name"
            className="rounded-xl"
          />
        </div>

        <div className="pt-4 border-t border-border">
          <div className="flex items-start gap-3">
            <Checkbox
              id="consent"
              checked={consent}
              onCheckedChange={(checked) => setConsent(checked === true)}
              className="mt-0.5"
            />
            <label htmlFor="consent" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
              I understand that my individual responses are confidential and will be used to generate 
              a personal leadership report. I also agree that my anonymised responses may be used for 
              research purposes and to improve the inventory algorithm for future users. This tool 
              supports discussion and development, not hiring decisions alone. <a href="/legal#privacy" className="text-primary hover:underline">Privacy Policy</a>
            </label>
          </div>
          {errors.consent && (
            <p className="text-sm text-destructive mt-1">{errors.consent}</p>
          )}
        </div>

        <Button
          type="submit"
          variant="hero"
          size="xl"
          className="w-full"
          disabled={loading}
        >
          {loading ? 'Starting...' : 'Start the Inventory'}
          <ArrowRight className="h-5 w-5" />
        </Button>
      </form>
    </div>
  );
}
